import Vue from 'vue';
import jwt_decode from "jwt-decode";
import Cookies from 'js-cookie';

var CookiesJS = Cookies.withAttributes({
  path: '/',
  secure: true,
});

const state = () => ({
  afterLogoutPathName: {
    default: 'login',
  },

  account: {},
  isAuthenticated: false,

  // Silently login setup
  silentlyLoginTimeout: null,
  secondsBeforeSilentlyLogin: 90,
  killSilentlyAfterTimes: 3,

  // Postback executed.
  postbackExecuted: false,
});

const getters = {
  token (state) {
    try {
      return state.account.authToken || null;
    } catch (err) {
      return null;
    }
  },
  isAdmin(state) {
    return (state.account.roles) ? (state.account.roles[0] === 'LhhpAdmin') : false;
  },
};

const mutations = {
  loadSession(state) {
    let account = {};
    if (typeof state.account.authToken === 'string') {
      return;
    }

    try {
      account = CookiesJS.get('account');
      if (account) {
        state.account = JSON.parse(account);
        state.isAuthenticated = true;
      }
    } catch (err) {
      console.warn(err);

      // Silence is power...
      state.account = {};
    }
  },
  
  updateAuth(state, payload) {
    try {
      const decodedToken = jwt_decode(payload.authToken);
      const type = decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];

      const timeToExpire = decodedToken.exp - decodedToken.nbf;
      const expiresAt = this.$dayjs().add(timeToExpire, 'second');

      payload.exp = expiresAt.format();
      payload.type = type;

      let tempAuth = {};

      Object.assign(tempAuth, state.account, payload);

      Vue.set(state, 'account', tempAuth);
      state.isAuthenticated = true;

      CookiesJS.set('account', JSON.stringify(state.account),  { expires: expiresAt.toDate() });
    } catch(err) {
      throw new Error(err);
    }
  },

  setSilentlyLoginTimeout(state, timeout) {
    state.silentlyLoginTimeout = timeout;
  },

  updateKillSilentlyAfterXTimes(state) {
    state.killSilentlyAfterTimes--;
  },

  setPostbackExecuted(state, value) {
    state.postbackExecuted = value;
  },
};

const actions = {
  async updateSession({ rootGetters, state, commit, dispatch }, payload) {
    const doNotRedirect = payload.doNotRedirect || false;
    if (doNotRedirect) {
      payload.doNotRedirect = null;
    }

    try {
      commit('updateAuth', payload);

      if (doNotRedirect) {
        dispatch('silentlyLoginTimeout');
      }

      if (!doNotRedirect) {
        let goto = { name: 'home' };

        await dispatch('users/getMe', undefined, { root: true });

        if (rootGetters['users/unfinished']) {
          console.log('users/unfinished', "Let's go to profile");
          goto = { name: 'profile', query: { incomplete: true }};
        } else if (state.account.roles[0] === 'LhhpAdmin') {
          goto = { name: 'home-admin' };
        }
        document.location = this.$router.resolve(goto).href;
      } else {
        console.log('Login Silently Works!');
      }
      return true;
    } catch (err) {
      this.$warning(`Oops! Something happened when loging you in, please try again,
if the problem persist contact the system administrator`);
      throw new Error(err);
    }
  },

  async authenticate({ dispatch }, token) {
    try {
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };

      const response = await this.$axios.post('b2c/authenticate', null, config);
      
      if (response.data.success) {
        await dispatch('updateSession', response.data);
      } else {
        this.$sCache.set('flash-message',
          { type: 'warning', message: `It seems that the user doesn't exist in our platform.` }
        );
        dispatch('phillyAccount/msalSignOut', {}, { root: true });
        return false;  
      }
    } catch (err) {
      console.log("authenticate err", err);
      this.$sCache.set('flash-message',
        { type: 'warning', message: `It seems that the user doesn't exist in our platform.` }
      );
      dispatch('phillyAccount/msalSignOut', {}, { root: true });
      return false;
    }
  },

  /**
   * Execute Login Silently
   */
  async runLoginSilently({ state, getters, commit, dispatch }) {
    clearTimeout(state.silentlyLoginTimeout);
    console.log('Running loging silently.');
    try {
      const config = {
        headers: { Authorization: `Bearer ${getters['token']}` },
      };
      const data = { refreshToken: getters['token'] };
      const response = await this.$axios.post('me/refresh-token', data, config);
      const newAuth = response.data;

      newAuth.doNotRedirect = true;

      await dispatch('updateSession', newAuth);
      return `It looks like everything goes good!`;
    } catch (err) {
      if (state.killSilentlyAfterTimes > 0) {
        dispatch('silentlyLoginTimeout');
        commit('updateKillSilentlyAfterXTimes');
      }
      throw new Error(err);
    }
  },

  /**
   * Set Silently Login Timeout
   */
  async silentlyLoginTimeout({ state, commit, dispatch }) {
    if (typeof state.account.exp !== 'undefined') {
      try {
        const expire_time = this.$dayjs(state.account.exp);
        const diff = expire_time.diff(this.$dayjs(), 'seconds');
        console.warn(`AUTOLOGIN IN: ${diff}`);
        if (diff > state.secondsBeforeSilentlyLogin) {
          if (state.silentlyLoginTimeout) {
            clearTimeout(state.silentlyLoginTimeout);
          }
          const tempTimeout = setTimeout(() => {
            dispatch('runLoginSilently');
          }, ((diff - state.secondsBeforeSilentlyLogin) * 1000));
          commit('setSilentlyLoginTimeout', tempTimeout);
        } else {
          dispatch('runLoginSilently');
        }
        return true;
      } catch (err) {
        console.warn(err);
        throw new Error(err);
      }
    }
  },

  ssoSignInErrorHandler({ commit, dispatch }, error) {
    if (error.errorCode === 'no_cached_authority_error') {
      // There is a very weird error happening with the cache and there isn't enough information online about this. 
      // The temporary solution for now is to re-trigger the signIn since the user is already logged in and this should refresh the cache and work.
      dispatch('phillyAccount/msalSignIn', {}, { root: true });
      return;
    } else if (error.errorMessage.indexOf("AADB2C90091") === -1) { // The error message AADB2C90091 is when the user cancels the action, not need of error notification.
      this.$warning('Something went wrong. Please refresh your page and try again. If you\'re unable to proceed, <a href="/contact-us" target="_blank">contact us</a>.', { duration: 10000 });
    }
    commit('phillyAccount/setRedirectingForgotPassword', false, { root: true });
    commit('phillyAccount/setSigningIn', false,  { root: true });
    console.warn(error);
  },

  async signOut() {
    try {
      CookiesJS.remove('account');
    } catch (err) {
      // Silence is golden!
    }
    return;
  },

  endOfActions({ state, commit }) {
    console.log("It is landing in here...");
    if (!state.postbackExecuted) {
      this.$success("Password has been reset successfully.");
    }
    commit('setPostbackExecuted', true);
    commit('phillyAccount/setRedirectingForgotPassword', false, { root: true });
    commit('phillyAccount/setSigningIn', false,  { root: true });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};